import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MenuBar } from '../../components/ui/glow-menu';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircle,
  Home,
  Video,
  Handshake,
  Radio,
  LogIn,
  Send,
} from 'lucide-react';
import logoImg from '../../assets/images/logo.svg';

const API_URL = 'https://apertai.uc.r.appspot.com';

const menuItems = [
  {
    icon: Home,
    label: 'Início',
    href: '/',
    gradient:
      'radial-gradient(circle, rgba(245,138,37,0.2) 0%, rgba(229,118,5,0.08) 50%, transparent 100%)',
    iconColor: 'text-orange-500',
  },
  {
    icon: Video,
    label: 'Vídeos',
    href: '/videos',
    gradient:
      'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, transparent 100%)',
    iconColor: 'text-blue-500',
  },
  {
    icon: Radio,
    label: 'Ao Vivo',
    href: '/livestream',
    gradient:
      'radial-gradient(circle, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.06) 50%, transparent 100%)',
    iconColor: 'text-red-500',
  },
  {
    icon: Handshake,
    label: 'Contratar',
    href: '/contract-camera',
    gradient:
      'radial-gradient(circle, rgba(34,197,94,0.15) 0%, rgba(22,163,74,0.06) 50%, transparent 100%)',
    iconColor: 'text-green-500',
  },
  {
    icon: LogIn,
    label: 'Entrar',
    href: '/login',
    gradient:
      'radial-gradient(circle, rgba(168,85,247,0.15) 0%, rgba(147,51,234,0.06) 50%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
];

function ContractCamera() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    arena_name: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleMenuClick = (label: string) => {
    const item = menuItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name || !formData.phone) {
      setError('Por favor, preencha pelo menos seu nome e telefone.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Erro ao enviar');

      setIsSubmitted(true);
    } catch {
      setError('Erro ao enviar formulário. Tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const GlowHeader = () => (
    <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5" style={{ backgroundColor: 'rgba(10,10,10,0.8)' }}>
      <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
      <MenuBar items={menuItems} activeItem="Contratar" onItemClick={handleMenuClick} />
    </header>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <GlowHeader />
        <div className="text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">Recebemos seu contato!</h2>
          <p className="text-zinc-400 mb-4">
            Nossa equipe entrará em contato em breve. Obrigado pelo interesse!
          </p>
          <p className="text-zinc-500 text-sm mb-8">
            Enquanto isso, conheça o ArenAi — o sistema completo de gestão para quadras esportivas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/"
              className="inline-block px-8 py-3 rounded-lg font-semibold text-white no-underline text-center"
              style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
            >
              Voltar ao início
            </a>
            <a
              href="https://arenai.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 rounded-lg font-semibold text-zinc-300 border border-zinc-700 no-underline text-center hover:border-yellow-500/50"
            >
              Conhecer o ArenAi
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      <GlowHeader />

      <div className="flex items-center justify-center min-h-screen px-4 py-24">
        <div className="mx-auto max-w-3xl w-full">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-3">
              Quero contratar
            </h1>
            <p className="text-zinc-500 text-base md:text-lg max-w-xl mx-auto">
              Preencha o formulário e nossa equipe entra em contato com você.
              Sem compromisso, sem custo de implantação.
            </p>
          </div>

          {/* Form card */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-6 md:p-10 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="full_name">Nome completo *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    placeholder="Seu nome"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone">Telefone / WhatsApp *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(31) 99999-9999"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="arena_name">Nome da arena / quadra</Label>
                  <Input
                    id="arena_name"
                    name="arena_name"
                    type="text"
                    placeholder="Ex: Arena Beach Tennis"
                    value={formData.arena_name}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="message">Mensagem</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Conte um pouco sobre sua arena, quantas quadras tem, etc..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 font-semibold px-8 py-3.5 rounded-lg text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border-none cursor-pointer text-base disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
              >
                <Send className="w-4 h-4" />
                {isSubmitting ? 'Enviando...' : 'Entre em contato comigo'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContractCamera;
